import {
  createContext,
  Dispatch,
  FC,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  iDataNivel,
  iDataNivelAsignatura,
} from "../../../interfaces/SuperAdmin/Pages/iDataNivel";
import { iDataTipo } from "../../../interfaces/SuperAdmin/Pages/iDataTipos";
import { useParams } from "react-router-dom";
import {
  getNivelesByOrgId,
  getAsignaturasByNivelId,
} from "../../../api/broker/nivel";
import { getTiposByOrgId } from "../../../api/broker/tipos";
import { toast } from "react-toastify";
import { PostObjetivoFields } from "../../../interfaces/SuperAdmin/Pages/iDataObjetivos";
import { postObjetivo } from "../../../api/broker/objetivo";

interface CreateObjetivoContextProps {
  niveles: iDataNivel[];
  nivelAsigns: iDataNivelAsignatura[];
  tipos: iDataTipo[];
  indicadores: string[];

  nombre: string;
  codigo: string;
  selectedNivel: string;
  selectedNivelAsign: string;
  selectedTipo: string;

  isLoadingNiveles: boolean;
  isLoadingNivelAsigns: boolean;
  isCreating: boolean;
  isAdding: boolean;
  isFormValid: boolean;

  handleNombreChange: (nombre: string) => void;
  handleCodigoChange: (codigo: string) => void;
  handleSelectNivel: (id_nivel: string) => void;
  handleSelectNivelAsign: (id_nivelAsign: string) => void;
  handleSelectTipo: (id_tipo: string) => void;
  addIndicador: (valor: string) => void;
  editIndicador: (index: number, valor: string) => void;
  deleteIndicador: (index: number) => void;
  objetivoCreate: () => Promise<void>;
  setIsAdding: Dispatch<SetStateAction<boolean>>;
}

const CreateObjetivoContext = createContext<CreateObjetivoContextProps>(
  {} as CreateObjetivoContextProps
);

interface Props {
  children: React.ReactNode;
}

const CreateObjetivoProvider: FC<Props> = ({ children }) => {
  const { idOrganization } = useParams();

  // Estados para las variables del contexto
  const [niveles, setNiveles] = useState<iDataNivel[]>([]);
  const [nivelAsigns, setNivelAsigns] = useState<iDataNivelAsignatura[]>([]);
  const [tipos, setTipos] = useState<iDataTipo[]>([]);
  const [indicadores, setIndicadores] = useState<string[]>([]);

  const [nombre, setNombre] = useState<string>("");
  const [codigo, setCodigo] = useState<string>("");
  const [selectedNivel, setSelectedNivel] = useState<string>("");
  const [selectedNivelAsign, setSelectedNivelAsign] = useState<string>("");
  const [selectedTipo, setSelectedTipo] = useState<string>("");

  const [isLoadingNiveles, setIsLoadingNiveles] = useState<boolean>(false);
  const [isLoadingNivelAsigns, setIsLoadingNivelAsigns] =
    useState<boolean>(false);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [isAdding, setIsAdding] = useState<boolean>(false);

  // Validación del formulario
  const isFormValid =
    !isAdding &&
    nombre.trim() !== "" &&
    codigo.trim() !== "" &&
    selectedNivel !== "" &&
    selectedNivelAsign !== "" &&
    selectedTipo !== "";

  // Fetch de niveles y tipos de la organización
  const fetchNiveles = async (id: string) => {
    try {
      setIsLoadingNiveles(true);
      const [nivelesRes, tiposRes] = await Promise.all([
        getNivelesByOrgId(id),
        getTiposByOrgId(id),
      ]);

      console.log(nivelesRes);
      if (nivelesRes.data.statusCode === 200) {
        setNiveles(nivelesRes.data.body);
      } else {
        toast.error("Error al obtener los niveles");
      }

      console.log(tiposRes);
      if (tiposRes.data.statusCode === 200) {
        setTipos(tiposRes.data.body);
      } else {
        toast.error("Error al obtener las asignaturas del nivel");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al obtener niveles y tipos");
    } finally {
      setIsLoadingNiveles(false);
    }
  };

  // Fetch de asignaturas por nivel
  const fetchNivelAsigns = async (id_nivel: string) => {
    try {
      setIsLoadingNivelAsigns(true);
      const nivelAsignsResponse = await getAsignaturasByNivelId(id_nivel);

      console.log(nivelAsignsResponse);
      if (nivelAsignsResponse.data.statusCode === 200) {
        setNivelAsigns(nivelAsignsResponse.data.body);
      } else {
        toast.error("Error al obtener las asignaturas del nivel");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al obtener las asignaturas del nivel");
    } finally {
      setIsLoadingNivelAsigns(false);
    }
  };

  // Función para cambiar el nombre del objetivo
  const handleNombreChange = (nombre: string) => {
    setNombre(nombre);
  };

  // Función para cambiar el código del objetivo
  const handleCodigoChange = (codigo: string) => {
    setCodigo(codigo);
  };

  // Función para seleccionar un nivel
  const handleSelectNivel = (id_nivel: string) => {
    setSelectedNivel(id_nivel);
  };

  // Función para seleccionar una asignatura de nivel
  const handleSelectNivelAsign = (id_nivelAsign: string) => {
    setSelectedNivelAsign(id_nivelAsign);
  };

  // Función para seleccionar un tipo
  const handleSelectTipo = (id_tipo: string) => {
    setSelectedTipo(id_tipo);
  };

  const objetivoCreate = async () => {
    if (idOrganization) {
      const body: PostObjetivoFields = {
        nombre,
        id_nivel_asignatura: selectedNivelAsign,
        id_tipo: selectedTipo,
        codigo,
        indicadores,
      };

      console.log(body);
      setIsCreating(true);
      try {
        //Creamos la asignatura
        const res = await postObjetivo(body, idOrganization);
        console.log(res);

        if (res.data.statusCode === 201) {
          toast.success("Objetivo creado exitosamente");
          setIndicadores([]);
          setNombre("");
          setCodigo("");
          setSelectedNivel("");
          setSelectedNivelAsign("");
          setSelectedTipo("");
        } else {
          toast.error("Error al crear objetivo en la organización");
        }
      } catch (error) {
        console.log(error);
        toast.error("Error al crear objetivo en la organización");
      } finally {
        setIsCreating(false);
      }
    }
  };

  // Funcion para agregar indicador
  const addIndicador = (valor: string) => {
    if (!valor.trim()) return;
    setIndicadores((prev) => [...prev, valor.trim()]);
  };

  // Funcion para editar indicador
  const editIndicador = (index: number, valor: string) => {
    setIndicadores((prev) =>
      prev.map((ind, i) => (i === index ? valor.trim() : ind))
    );
  };

  // Funcion para eliminar indicador
  const deleteIndicador = (index: number) => {
    setIndicadores((prev) => prev.filter((_, i) => i !== index));
  };

  // Cada vez que se monta, se obtienen niveles y tipos
  useEffect(() => {
    if (idOrganization) {
      fetchNiveles(idOrganization);
    }
  }, [idOrganization]);

  // Cuando se selecciona un nivel se obtienen sus asignaturas
  useEffect(() => {
    if (selectedNivel) {
      fetchNivelAsigns(selectedNivel);
    }
  }, [selectedNivel]);

  return (
    <CreateObjetivoContext.Provider
      value={{
        niveles,
        nivelAsigns,
        tipos,
        indicadores,
        nombre,
        codigo,
        selectedNivel,
        selectedNivelAsign,
        selectedTipo,
        isLoadingNiveles,
        isLoadingNivelAsigns,
        isCreating,
        isFormValid,
        isAdding,
        handleNombreChange,
        handleCodigoChange,
        handleSelectNivel,
        handleSelectNivelAsign,
        handleSelectTipo,
        addIndicador,
        editIndicador,
        deleteIndicador,
        objetivoCreate,
        setIsAdding,
      }}
    >
      {children}
    </CreateObjetivoContext.Provider>
  );
};

const useCreateObjetivo = () => useContext(CreateObjetivoContext);

export { CreateObjetivoProvider, useCreateObjetivo };
